/**
 * UsageIndicator Component
 *
 * Inline display of feature usage against tier limits.
 * Shows nothing for unlimited or locked features.
 * Auto-fetches usage data on first mount.
 */

import React, { useEffect, useRef } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { Lock } from 'lucide-react-native';

import { useThemeColors } from '@/lib/useThemeColors';
import { useSubscriptionStore } from '@/lib/state/subscription-store';
import { useUsageCount, useUsageStore } from '@/lib/state/usage-store';
import type { FeatureKey } from '@/lib/subscription/features';

interface UsageIndicatorProps {
  feature: FeatureKey;
  suffix?: string;
}

export function UsageIndicator({ feature, suffix }: UsageIndicatorProps) {
  const colors = useThemeColors();
  const limit = useSubscriptionStore((s) => s.getFeatureLimit(feature));
  const usage = useUsageCount(feature);
  const fetchUsage = useUsageStore((s) => s.fetchUsage);
  const hasFetched = useRef(false);

  // Auto-fetch usage data on first mount
  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchUsage();
    }
  }, [fetchUsage]);

  // Don't render for unlimited (-1) or locked (0) features, or admin (Infinity)
  if (limit === -1 || limit === 0 || limit === Infinity) return null;

  const atLimit = usage >= limit;
  const nearLimit = usage / limit >= 0.8;

  const textColor = atLimit
    ? '#EF4444'
    : nearLimit
    ? '#F59E0B'
    : colors.text.tertiary;

  if (atLimit) {
    return (
      <View style={styles.container}>
        <Lock size={12} color={textColor} />
        <Text numberOfLines={1} style={[styles.text, { color: textColor }]}>
          Limit reached
        </Text>
      </View>
    );
  }

  const suffixText = suffix ? ` ${suffix}` : '';

  return (
    <View style={styles.container}>
      <Text numberOfLines={1} style={[styles.text, { color: textColor }]}>
        {usage}/{limit} used{suffixText}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  text: {
    fontSize: 12,
    fontWeight: '500',
  },
});
